import { useState, useRef, useEffect } from "react";
import { ArrowLeft, Flag, Undo2, Wifi, WifiOff, Heart, Flame, HelpCircle, Trophy, ChevronsDownUp, ChevronsUpDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SessionState, ScoreVisibility, Category, WsEvent, filledCount } from "../../types/game";
import { WsStatus } from "../../hooks/useWebSocket";
import { useHaptic } from "../../hooks/useHaptic";
import { useSound } from "../../hooks/useSound";
import { useGameNotifications } from "../../hooks/useGameNotifications";
import Scorecard from "./Scorecard";
import ScoreInputSheet from "./ScoreInputSheet";
import WinnerScreen from "../winner/WinnerScreen";
import GameNotification from "./GameNotification";
import FontScaler from "../common/FontScaler";
import LanguageSwitcher from "../common/LanguageSwitcher";
import AdminSettingsModal from "./AdminSettingsModal";
import { formatGameDate } from "../../utils/date";
import { getTheme } from "../../utils/themes";
import DiceTray from "./DiceTray";
import ReactionOverlay from "./ReactionOverlay";
import RulesModal from "../common/RulesModal";

import { Tournament, TournamentMatrix } from "../../types/tournament";
import TournamentStandings from "./TournamentStandings";

interface Props {
  state: SessionState | null;
  myUserId: string | null;
  myTelegramId: number | null;
  isAdmin: boolean;
  token?: string;
  colorfulDice?: boolean;
  soundEnabled?: boolean;
  tableTheme?: string;
  suggestions?: Record<string, number>;
  tournament?: Tournament | null;
  tournamentMatrix?: TournamentMatrix | null;
  onUpdateScore: (
    player_id: string,
    category: string,
    value: number | null,
    fromHand?: boolean,
  ) => void;
  onFinishGame: () => void;
  onLeave: () => void;
  onCreateNewGame: () => void;
  onRematch: () => void;
  onUpdateSettings: (sv?: ScoreVisibility, gm?: string) => void;
  onToggleColorful?: (val: boolean) => void;
  onRollDice: () => void;
  onToggleHold: (index: number) => void;
  onReact?: (emoji: string) => void;
  onReorderPlayers?: (ids: string[]) => void;
  onRemovePlayer?: (playerId: string) => void;
  onAddRealPlayer?: (userId: string, displayName?: string) => void;
  onAddPhantomPlayer?: (displayName: string, avatar: string) => void;
  onNotifyResults?: () => Promise<void>;
  onShowTournamentStandings?: () => void;
  onNextTournamentGame?: () => void;
  onFinishTournament?: () => void;
  onViewMatch?: (sessionId: string) => void;
  wsStatus?: WsStatus;
  queuedCount?: number;
  latestEvent?: WsEvent | null;
}

export default function GameView({
  state,
  myUserId,
  isAdmin,
  token,
  colorfulDice,
  soundEnabled = true,
  tableTheme = "classic",
  suggestions = {},
  tournament,
  tournamentMatrix,
  onUpdateScore,
  onFinishGame,
  onLeave,
  onCreateNewGame,
  onRematch,
  onUpdateSettings,
  onToggleColorful,
  onRollDice,
  onToggleHold,
  onReact,
  onReorderPlayers,
  onRemovePlayer,
  onAddRealPlayer,
  onAddPhantomPlayer,
  onNotifyResults,
  onShowTournamentStandings,
  onNextTournamentGame,
  onFinishTournament,
  onViewMatch,
  wsStatus = "connected",
  queuedCount = 0,
  latestEvent = null,
}: Props) {
  const { t, i18n } = useTranslation();
  const theme = getTheme(tableTheme);
  const { impact, notification } = useHaptic();
  const { play } = useSound(soundEnabled);
  
  const [showConfirm, setShowConfirm] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [showAdminSettings, setShowAdminSettings] = useState(false);
  // Per-user table density preference (persisted on this device)
  const [compact, setCompact] = useState(() => {
    try { return localStorage.getItem("poker_dice_compact") === "1"; } catch { return false; }
  });
  const toggleCompact = () => {
    setCompact((c) => {
      const next = !c;
      try { localStorage.setItem("poker_dice_compact", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };
  const [showTournamentStandings, setShowTournamentStandings] = useState(false);
  const [showWinner, setShowWinner] = useState(false);
  const [prevStatus, setPrevStatus] = useState<string | null>(null);
  const [editingCell, setEditingCell] = useState<{ playerId: string; category: Category } | null>(null);
  
  const [lastMove, setLastMove] = useState<{
    playerId: string;
    category: string;
    previousValue: number | null;
    previousFromHand: boolean;
  } | null>(null);

  const [latestReaction, setLatestReaction] = useState<{ emoji: string, from: string } | null>(null);
  const [editToast, setEditToast] = useState<{ editor: string; target: string; category: string } | null>(null);
  const editToastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync state-dependent local state
  useEffect(() => {
    if (!state) return;
    
    if (state.session.status === "finished" && prevStatus !== "finished") {
        setShowWinner(true);
    }
    setPrevStatus(state.session.status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.session.status, prevStatus]);

  // Handle reactions from WS events
  useEffect(() => {
    if (latestEvent?.type === "reaction") {
      setLatestReaction({ emoji: latestEvent.emoji, from: latestEvent.from });
    }
  }, [latestEvent]);

  // Announce edits made by someone else on a row that isn't their own —
  // free-mode shared scorekeeping is last-write-wins, so make it visible.
  useEffect(() => {
    if (latestEvent?.type !== "score_updated" || !latestEvent.changed_by || !state) return;
    const { changed_by, player_id, category } = latestEvent;
    if (changed_by.user_id === myUserId) return;
    const row = state.players_with_scorecards.find((p) => p.player.id === player_id);
    if (!row || row.player.user_id === changed_by.user_id) return;
    setEditToast({ editor: changed_by.display_name, target: row.player.display_name, category });
    if (editToastTimer.current) clearTimeout(editToastTimer.current);
    editToastTimer.current = setTimeout(() => setEditToast(null), 3500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestEvent]);

  // ── Game event notifications ────────────────────────────────
  const { notifications, checkEvents, pushAchievement, dismiss } = useGameNotifications();
  const prevStateRef = useRef<SessionState | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevNotifCount = useRef(0);

  // Check for events whenever state changes
  useEffect(() => {
    if (state && prevStateRef.current !== state) {
      checkEvents(prevStateRef.current, state);
      prevStateRef.current = state;
    }
  }, [state, checkEvents]);

  // Play sounds when new notifications appear
  useEffect(() => {
    if (notifications.length > prevNotifCount.current) {
      const newest = notifications[notifications.length - 1];
      if (newest.type === "achievement_unlocked") {
        play("achievement");
        notification("success");
      } else if (newest.type === "school_honors") {
        play("school_good");
        notification("success");
      } else if (newest.type === "school_flunker") {
        play("school_bad");
        notification("warning");
      } else if (newest.type === "school_graduated") {
        play("score");
      } else if (newest.type === "main_game_started") {
        play("clink");
        notification("success");
      } else if (newest.type === "poker_rolled") {
        play("win");
        notification("success");
      } else if (newest.type === "take_the_lead") {
        play("clink");
      } else if (newest.type === "last_round") {
        play("score");
        notification("warning");
      } else if (newest.type === "crossed_out") {
        play("school_bad");
      }
    }
    prevNotifCount.current = notifications.length;
  }, [notifications, play, notification]);

  // Handle achievement notifications from WS events
  useEffect(() => {
    if (state && latestEvent?.type === "game_finished" && latestEvent.new_achievements) {
      Object.entries(latestEvent.new_achievements).forEach(([uid, achs]) => {
        const player = state.players_with_scorecards.find(p => p.player.user_id === uid);
        if (player) {
          achs.forEach(ach => {
            pushAchievement(
              player.player.display_name,
              player.player.avatar,
              i18n.language === 'ru' ? ach.name_ru : ach.name_en,
              ach.icon
            );
          });
        }
      });
    }
  }, [latestEvent, state, i18n.language, pushAchievement]);

  // Scroll to top once when game data first becomes available so the dice tray is visible.
  const scrolledToTopOnMount = useRef(false);
  useEffect(() => {
    if (state && !scrolledToTopOnMount.current && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
      scrolledToTopOnMount.current = true;
    }
  }, [state]);

  // Scroll to top when it becomes the player's turn in strict mode so the dice tray is visible.
  useEffect(() => {
    if (!state || state.session.game_mode !== "strict") return;
    const myPlayer = state.players_with_scorecards.find(p => p.player.user_id === myUserId);
    const myPlayerId = myPlayer?.player.id ?? null;
    if (state.session.current_turn_player_id === myPlayerId && scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.session.current_turn_player_id, state?.session.game_mode, myUserId]);

  // Loading Guard - MUST BE AFTER ALL HOOKS
  if (!state) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  const finished = state.session.status === "finished";
  const myPlayer = state.players_with_scorecards.find(
    (p) => p.player.user_id === myUserId,
  );
  const myPlayerId = myPlayer?.player.id ?? null;
  const sv: ScoreVisibility = state.session.score_visibility ?? "realtime";

  const isStrict = state.session.game_mode === "strict";
  const isMyTurn = isStrict && state.session.current_turn_player_id === myPlayerId;
  const editingPlayer = editingCell
    ? state.players_with_scorecards.find((p) => p.player.id === editingCell.playerId) ?? null
    : null;

  // Calculate current round and if game is fully completed.
  // filledCount works on redacted final_only payloads too.
  const turnCounts = state.players_with_scorecards.map((p) => filledCount(p));
  const maxTurns = Math.max(0, ...turnCounts);
  const minTurns = turnCounts.length > 0 ? Math.min(...turnCounts) : 0;
  
  const currentRound = Math.min(maxTurns + 1, 15);
  const allFinishedLocally = minTurns === 15;

  const handleUpdateScore = (playerId: string, category: string, value: number | null, fromHand: boolean = false) => {
    const player = state.players_with_scorecards.find(p => p.player.id === playerId);
    const prev = player?.scorecard[category as Category] ?? null;
    const prevFromHand = player?.scorecard.from_hand?.includes(category) ?? false;
    if (prev !== value || prevFromHand !== fromHand) {
      setLastMove({ playerId, category, previousValue: prev, previousFromHand: prevFromHand });
      onUpdateScore(playerId, category, value, fromHand);
      // Haptic & Sound
      impact("light");
      play("click");
    }
  };

  const handleUndo = () => {
    if (lastMove) {
      onUpdateScore(lastMove.playerId, lastMove.category, lastMove.previousValue, lastMove.previousFromHand);
      setLastMove(null);
      impact("medium");
    }
  };

  return (
    <div className={`h-game-view relative text-ink flex flex-col transition-colors duration-700`}>
      {/* WS reconnect banner */}
      {wsStatus !== "connected" && (
        <div className="fixed top-0 left-0 right-0 z-50 flex flex-col items-center justify-center gap-1 py-2 px-4 text-sm font-medium text-white"
          style={{ background: wsStatus === "disconnected" ? "#dc2626" : "#d97706" }}>
          <div className="flex items-center gap-2">
            {wsStatus === "disconnected" ? <WifiOff size={14} /> : <Wifi size={14} />}
            {wsStatus === "disconnected"
              ? t("game.reconnecting", "Reconnecting…")
              : t("game.connecting", "Connecting…")}
          </div>
          {queuedCount > 0 && (
            <div className="text-[10px] bg-black/20 px-2 py-0.5 rounded-full">
              {queuedCount} {t("game.pendingUpdates", "pending updates")}
            </div>
          )}
        </div>
      )}
      {/* Spectator banner */}
      {!myPlayerId && state.session.status === "active" && (
        <div className="bg-purple-900/80 border-b border-purple-500/30 text-purple-300 text-xs font-bold text-center py-1.5 tracking-wider uppercase">
          👁 {t("tournament.spectatorMode")}
        </div>
      )}
      {/* Top bar */}
      <div className="glass-header flex items-center justify-between px-4 py-3 sticky top-0 z-20">
        <button
          onClick={onLeave}
          className="p-2 text-ink/50 hover:text-ink transition-colors -ml-2"
        >
          <ArrowLeft size={20} />
        </button>
        <div 
          className={`text-center ${isAdmin && !finished ? "cursor-pointer hover:opacity-80" : ""}`}
          onClick={() => isAdmin && !finished && setShowAdminSettings(true)}
        >
          <p className="font-bold text-sm">
            {finished ? t("game.gameOver") : `🎲 ${t("app.title")} — ${t("game.round")} ${currentRound}/15`}
          </p>
          <p className={`text-xs ${theme.accentClass} font-bold`}>
            {formatGameDate(state.session.created_at, i18n.language)}
            {isAdmin && !finished && <span className="ml-1 text-[10px] bg-blue-900/50 px-1 rounded border border-blue-800 font-mono">{t("game.edit")}</span>}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {tournament && (
            <button
              onClick={() => setShowTournamentStandings(true)}
              className="p-2 text-yellow-500 hover:text-yellow-400 transition-colors"
              title={t("tournament.standings")}
            >
              <Trophy size={18} />
            </button>
          )}
          <button
            onClick={toggleCompact}
            className={`p-2 transition-colors ${compact ? "text-blue-400" : "text-ink/50 hover:text-ink"}`}
            title={t("game.compactView")}
          >
            {compact ? <ChevronsUpDown size={18} /> : <ChevronsDownUp size={18} />}
          </button>
          <button
            onClick={() => setShowRules(true)}
            className="p-2 text-ink/50 hover:text-ink transition-colors"
            title={t("lobby.rules", "Rules")}
          >
            <HelpCircle size={18} />
          </button>
          <FontScaler />
          <LanguageSwitcher />
          {isAdmin && !finished && (
            <button
              onClick={() => setShowConfirm(true)}
              className="p-2 text-ink/50 hover:text-red-400 transition-colors"
            >
              <Flag size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Someone edited another player's row */}
      {editToast && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-40 glass px-4 py-2 rounded-full text-xs text-ink/80 shadow-lg animate-in fade-in slide-in-from-top-2 duration-300 whitespace-nowrap">
          ✏️ {t("game.editedBy", {
            editor: editToast.editor,
            target: editToast.target,
            category: t(`categories.${editToast.category}`),
          })}
        </div>
      )}

      {/* Scorecard */}
      <div ref={scrollRef} className="flex-1 overflow-auto p-3 space-y-4">
        {isStrict && !finished && (
          <DiceTray
            dice={state.session.current_dice || [1, 1, 1, 1, 1]}
            holds={state.session.dice_holds || [false, false, false, false, false]}
            rollCount={state.session.roll_count}
            isMyTurn={isMyTurn}
            isAdmin={isAdmin}
            colorfulDice={colorfulDice}
            soundEnabled={soundEnabled}
            onRoll={onRollDice}
            onToggleHold={onToggleHold}
          />
        )}

        <Scorecard
          players={state.players_with_scorecards}
          compact={compact}
          myPlayerId={myPlayerId}
          isAdmin={isAdmin}
          gameFinished={finished}
          scoreVisibility={sv}
          onCellClick={(playerId, category) => setEditingCell({ playerId, category })}
        />
        
        {/* Floating Undo Button */}
        {lastMove && !finished && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 animate-in fade-in slide-in-from-bottom-4 duration-300">
            <button
              onClick={handleUndo}
              className="glass flex items-center gap-2 text-ink/80 hover:text-ink px-5 py-2.5 rounded-full shadow-lg font-medium transition-colors"
            >
              <Undo2 size={18} />
              {t("game.undo", "Undo")}
            </button>
          </div>
        )}

        {/* 'Show Results' Button when everyone finishes */}
        {allFinishedLocally && !finished && (
          <div className="fixed bottom-4 left-4 right-4 z-40 glass-strong rounded-2xl p-4 flex flex-col items-center animate-in slide-in-from-bottom-10 fade-in duration-300">
            {isAdmin ? (
              <button
                onClick={() => setShowConfirm(true)}
                className="w-full py-4 rounded-xl font-bold text-xl text-white transition-all active:scale-95 animate-pulse"
                style={{
                  background: "linear-gradient(135deg, rgba(59,130,246,0.9), rgba(99,102,241,0.9))",
                  boxShadow: "0 8px 32px rgba(59,130,246,0.4), inset 0 1px 0 rgba(255,255,255,0.2)",
                }}
              >
                {t("game.showResults")}
              </button>
            ) : (
              <div className="glass text-center w-full py-4 rounded-xl">
                <span className="font-semibold text-ink/60 text-lg">{t("game.waitingForAdminFinish")}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Admin Settings Modal */}
      {showAdminSettings && (
        <AdminSettingsModal
          state={state}
          token={token}
          colorfulDice={colorfulDice}
          onUpdateSettings={onUpdateSettings}
          onToggleColorfulDice={(val) => {
            onToggleColorful?.(val);
          }}
          onReorderPlayers={onReorderPlayers}
          onRemovePlayer={onRemovePlayer}
          onAddReal={onAddRealPlayer}
          onAddPhantom={onAddPhantomPlayer}
          onClose={() => setShowAdminSettings(false)}
        />
      )}

      {/* Finish confirm modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="glass-modal rounded-2xl p-6 w-full max-w-sm">
            <h3 className="font-bold text-lg mb-2">
              {t("game.confirmFinishTitle")}
            </h3>
            <p className="text-ink/50 text-sm mb-6">
              {t("game.confirmFinishDesc")}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="glass flex-1 py-3 rounded-xl text-ink/70 hover:text-ink transition-colors"
              >
                {t("game.cancel")}
              </button>
              <button
                onClick={() => {
                  setShowConfirm(false);
                  onFinishGame();
                }}
                className="flex-1 py-3 rounded-xl font-semibold text-white transition-all active:scale-95"
                style={{
                  background: "linear-gradient(135deg, rgba(220,38,38,0.85), rgba(185,28,28,0.85))",
                  border: "1px solid rgba(220,38,38,0.4)",
                  boxShadow: "0 4px 16px rgba(220,38,38,0.3)",
                }}
              >
                {t("game.finish")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Winner overlay — full-screen animated reveal */}
      {showWinner && (
        <WinnerScreen
          players={state.players_with_scorecards}
          isAdmin={isAdmin}
          soundEnabled={soundEnabled}
          tournament={tournament}
          onShowStandings={tournament ? () => {
            setShowWinner(false);
            setShowTournamentStandings(true);
            onShowTournamentStandings?.();
          } : undefined}
          onNextTournamentGame={() => {
            setShowWinner(false);
            onNextTournamentGame?.();
          }}
          onFinishTournament={onFinishTournament ? () => {
            setShowWinner(false);
            onFinishTournament();
          } : undefined}
          onCreateNewGame={() => {
            setShowWinner(false);
            onCreateNewGame();
          }}
          onRematch={() => {
            setShowWinner(false);
            onRematch();
          }}
          onBack={() => {
            setShowWinner(false);
            onLeave();
          }}
          onNotifyResults={onNotifyResults}
          onClose={() => setShowWinner(false)}
        />
      )}

      {/* Tournament Standings Overlay */}
      {showTournamentStandings && tournament && tournamentMatrix && (
        <div className="fixed inset-0 z-[60] overflow-y-auto animate-in fade-in duration-300 p-4" style={{background:"rgba(8,9,26,0.97)",backdropFilter:"blur(24px)"}}>
          <div className="max-w-md mx-auto py-8">
            <button
              onClick={() => {
                if (state.session.status === "finished") {
                  setShowWinner(true);
                }
                setShowTournamentStandings(false);
              }}
              className="mb-6 flex items-center text-white/50 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              {t("game.cancel")}
            </button>
            <TournamentStandings 
              matrix={tournamentMatrix} 
              currentUserId={myUserId || undefined} 
              totalGames={tournament.total_games}
              currentGame={tournament.current_game_index}
              onMatchClick={onViewMatch}
            />
            
            <div className="mt-8 flex gap-3">
              <button
                onClick={onLeave}
                className="flex-1 py-4 glass text-white font-bold rounded-2xl hover:bg-white/10 transition-colors"
              >
                {t("winner.backToLobby")}
              </button>
              {isAdmin && tournament && (
                <button
                  disabled={tournament.current_game_index >= tournament.total_games}
                  onClick={() => {
                    setShowTournamentStandings(false);
                    onNextTournamentGame?.();
                  }}
                  className={`flex-1 py-4 bg-blue-600 text-white font-bold rounded-2xl ${
                    tournament.current_game_index >= tournament.total_games ? 'opacity-50' : ''
                  }`}
                >
                   {tournament.current_game_index >= tournament.total_games 
                    ? t('tournament.tournamentOver') 
                    : t('tournament.nextGame')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Game event notifications */}
      {notifications.map((notif) => (
        <GameNotification
          key={notif.id}
          notification={notif}
          onDismiss={dismiss}
        />
      ))}

      {showRules && <RulesModal onClose={() => setShowRules(false)} />}

      {/* Spectator Reactions */}
      {!myPlayerId && (
        <div className="fixed bottom-6 right-4 z-40 flex flex-col gap-2 animate-in slide-in-from-right-10 duration-500">
          {[
            { e: '❤️', icon: <Heart size={18} className="fill-rose-500 text-rose-500" /> },
            { e: '🔥', icon: <Flame size={18} className="fill-orange-500 text-orange-500" /> },
            { e: '👏', icon: <span className="text-lg">👏</span> },
            { e: '😱', icon: <span className="text-lg">😱</span> },
          ].map(r => (
            <button
              key={r.e}
              onClick={() => onReact?.(r.e)}
              className="glass w-12 h-12 rounded-full flex items-center justify-center shadow-xl active:scale-90 transition-all"
            >
              {r.icon}
            </button>
          ))}
        </div>
      )}

      <ReactionOverlay latestReaction={latestReaction} />

      {/* Score input sheet — rendered here as a sibling of the scroll area so it
          sits inside the position:relative h-game-view container but outside any
          glass/backdrop-filter ancestor. Uses position:absolute (not fixed) to
          avoid iOS WKWebView fixed-positioning bugs in Telegram Mini App. */}
      {editingCell && editingPlayer && (
        <ScoreInputSheet
          category={editingCell.category}
          current={editingPlayer.scorecard[editingCell.category as Category] as number | null}
          suggestedValue={isMyTurn ? suggestions[editingCell.category] : undefined}
          playerName={editingPlayer.player.display_name}
          currentFromHand={editingPlayer.scorecard.from_hand?.includes(editingCell.category) ?? false}
          onConfirm={(value, fromHand) => {
            handleUpdateScore(editingCell.playerId, editingCell.category, value, fromHand ?? false);
            setEditingCell(null);
          }}
          onClose={() => setEditingCell(null)}
        />
      )}
    </div>
  );
}
